import React, { useState, useEffect, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Modal } from '../ui/Modal';
import DropdownSearch from '../ui/DropdownSearch';
import FormField from '../forms/FormField';
import MultiSelectDropdownSearch from '../ui/MultiSelectDropdownSearch';
import { GitFork, Loader2 } from 'lucide-react';
import { projectRepositoriesAPI, organisationsAPI } from '../../services/api';
import toast from '../../services/toast';

/** Parse owner/repo from URL or "owner/repo" string */
function parseRepoIdentifier(input) {
  if (!input || typeof input !== 'string') return null;
  const t = input.trim();
  const urlMatch = t.match(/github\.com[/:]([^/]+)\/([^/]+?)(?:\.git)?$/i);
  if (urlMatch) return `${urlMatch[1]}/${urlMatch[2].replace(/\.git$/, '')}`;
  if (/^[^/]+\/[^/]+$/.test(t)) return t;
  return null;
}

const ConnectRepositoryToProject = ({ isOpen, onClose, orgId, streamId, projectId, onSuccess }: any) => {
  const [discoveredRepositories, setDiscoveredRepositories] = useState<any[]>([]);
  const [loadingDiscovered, setLoadingDiscovered] = useState(false);
  const [selectedRepo, setSelectedRepo] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState<any[]>([]);
  const [defaultBranch, setDefaultBranch] = useState('main');
  const [loadingBranches, setLoadingBranches] = useState(false);

  const [deploymentBranches, setDeploymentBranches] = useState<string[]>([]);
  const [customDeploymentBranchesText, setCustomDeploymentBranchesText] = useState('');

  const { register, control, watch, reset, handleSubmit } = useForm({
    defaultValues: {
      repository: '',
      qaBranch: '',
      uatBranch: '',
    },
  });
  const repository = watch('repository');

  const fetchDiscovered = useCallback(async () => {
    if (!orgId || !isOpen) return;
    setLoadingDiscovered(true);
    try {
      const { discoveredRepositories: list } =
        await organisationsAPI.getDiscoveredRepositories(orgId);
      setDiscoveredRepositories(Array.isArray(list) ? list : []);
      setSelectedRepo(null);
    } catch (err) {
      toast.error((err as Error).message || 'Failed to load repositories');
      setDiscoveredRepositories([]);
    } finally {
      setLoadingDiscovered(false);
    }
  }, [orgId, isOpen]);

  useEffect(() => {
    if (isOpen && orgId) fetchDiscovered();
  }, [isOpen, orgId, fetchDiscovered]);

  const canUseDiscovered = discoveredRepositories.length > 0;
  const repoIdentifier = canUseDiscovered
    ? (selectedRepo?.fullName ?? null)
    : parseRepoIdentifier(repository);

  useEffect(() => {
    if (!orgId || !repoIdentifier || !isOpen) {
      setBranches([]);
      setDefaultBranch('main');
      setDeploymentBranches([]);
      setCustomDeploymentBranchesText('');
      return;
    }
    setLoadingBranches(true);
    organisationsAPI
      .getRepositoryBranches(orgId, repoIdentifier)
      .then(({ branches: list, defaultBranch: def }) => {
        setBranches(Array.isArray(list) ? list : []);
        const branch = def || 'main';
        setDefaultBranch(branch);
        setDeploymentBranches([branch]);
        setCustomDeploymentBranchesText(branch);
      })
      .catch(() => {
        setBranches([]);
        setDefaultBranch('main');
        setDeploymentBranches([]);
        setCustomDeploymentBranchesText('');
      })
      .finally(() => setLoadingBranches(false));
  }, [orgId, repoIdentifier, isOpen]);

  const handleClose = () => {
    if (!loading && !loadingDiscovered) {
      setSelectedRepo(null);
      reset({ repository: '', qaBranch: '', uatBranch: '' });
      setDeploymentBranches([]);
      setCustomDeploymentBranchesText('');
      onClose();
    }
  };

  const resolveDeploymentBranchesForSubmit = () => {
    if (branches.length > 0) {
      return deploymentBranches.map((b) => b.trim()).filter(Boolean);
    }
    return customDeploymentBranchesText
      .split(',')
      .map((b) => b.trim())
      .filter(Boolean);
  };

  const canSubmitBranch = resolveDeploymentBranchesForSubmit().length > 0;

  const handleAddDiscovered = handleSubmit(async (values) => {
    if (!orgId || !projectId || !selectedRepo?.fullName || !canSubmitBranch) return;
    setLoading(true);
    try {
      const branchesToSend = resolveDeploymentBranchesForSubmit();
      const repo = await organisationsAPI.connectRepositoryToProject(orgId, {
        projectId,
        repository: selectedRepo.fullName,
        deploymentBranches: branchesToSend,
        qaBranch: values.qaBranch.trim() || undefined,
        uatBranch: values.uatBranch.trim() || undefined,
      });
      onClose();
      if (onSuccess) onSuccess(repo);
      toast.success('Repository added to project');
    } catch (err) {
      toast.error((err as Error).message || 'Failed to connect repository');
    } finally {
      setLoading(false);
    }
  });

  const handleAddByUrl = handleSubmit(async (values) => {
    const repoInput = values.repository.trim();
    if (!repoInput || !orgId || !streamId || !projectId || !canSubmitBranch) {
      toast.error('Please enter a repository URL or owner/repo and select a deployment branch');
      return;
    }
    setLoading(true);
    try {
      const branchesToSend = resolveDeploymentBranchesForSubmit();
      const repo = await projectRepositoriesAPI.connect(orgId, streamId, projectId, {
        repository: repoInput,
        deploymentBranches: branchesToSend,
        qaBranch: values.qaBranch.trim() || undefined,
        uatBranch: values.uatBranch.trim() || undefined,
      });
      reset({ repository: '', qaBranch: '', uatBranch: '' });
      setDeploymentBranches([]);
      setCustomDeploymentBranchesText('');
      onClose();
      if (onSuccess) onSuccess(repo);
      toast.success('Repository added to project');
    } catch (err) {
      toast.error((err as Error).message || 'Failed to connect repository');
    } finally {
      setLoading(false);
    }
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add repository to project"
      icon={<GitFork className="w-5 h-5 text-vivid-red" />}
      disableClose={loading || loadingDiscovered}
    >
      <>
        {loadingDiscovered ? (
          <div className="flex items-center gap-2 py-6 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Loading repositories…</span>
          </div>
        ) : canUseDiscovered ? (
          <>
            <p className="text-sm text-slate-400 mb-3">
              Choose a repository from your organisation to add to this project.
            </p>
            <form onSubmit={handleAddDiscovered} noValidate>
              <FormField id="connect-repo-select" label="Repository">
                <DropdownSearch
                  id="connect-repo-select"
                  options={discoveredRepositories}
                  value={selectedRepo?.fullName ?? null}
                  onChange={setSelectedRepo}
                  getOptionValue={(r) => r.fullName}
                  getOptionLabel={(r) => r.fullName}
                  getOptionSublabel={(r) =>
                    r.description
                      ? r.description.length > 60
                        ? `${r.description.slice(0, 60)}…`
                        : r.description
                      : ''
                  }
                  placeholder="Search repositories…"
                  emptyMessage="No repositories match your search"
                  disabled={loading}
                  className="mb-1"
                />
              </FormField>
              {selectedRepo && (
                <div className="mt-3 space-y-3">
                  <FormField id="connect-deployment-branches" label="Deployment branches (required)">
                    <p className="text-xs text-slate-400 mb-2">
                      First selected branch is primary for DORA labels and tech stack scans.
                    </p>
                    {loadingBranches ? (
                      <div className="text-sm text-slate-400 py-1">Loading branches…</div>
                    ) : branches.length > 0 ? (
                      <MultiSelectDropdownSearch
                        id="connect-deployment-branches"
                        options={branches}
                        selectedIds={deploymentBranches}
                        onChange={setDeploymentBranches}
                        getOptionValue={(b) => b.name}
                        getOptionLabel={(b) => b.name}
                        getOptionSublabel={(b) => (b.isDefault ? '(GitHub default)' : '')}
                        placeholder="Search branches to add…"
                        disabled={loading}
                        comboboxAriaLabel="Deployment branches"
                      />
                    ) : (
                      <input
                        id="connect-deployment-branches"
                        type="text"
                        value={customDeploymentBranchesText}
                        onChange={(e) => setCustomDeploymentBranchesText(e.target.value)}
                        placeholder={defaultBranch || 'e.g. main, release'}
                        className="input-mdl disabled:opacity-50"
                        disabled={loading}
                      />
                    )}
                  </FormField>
                  <FormField id="connect-qa-branch" label="QA branch (optional)">
                    {branches.length > 0 ? (
                      <Controller
                        name="qaBranch"
                        control={control}
                        render={({ field }) => (
                          <DropdownSearch
                            id="connect-qa-branch"
                            options={[{ name: '' }, ...branches]}
                            value={
                              field.value
                                ? (branches.find((b) => b.name === field.value) ?? {
                                    name: field.value,
                                  })
                                : { name: '' }
                            }
                            onChange={(b) => field.onChange(b?.name ?? '')}
                            getOptionValue={(b) => b.name}
                            getOptionLabel={(b) => b.name || '— None —'}
                            placeholder="Optional"
                            disabled={loading}
                            className="mb-1"
                          />
                        )}
                      />
                    ) : (
                      <input
                        id="connect-qa-branch"
                        type="text"
                        placeholder="Optional"
                        className="input-mdl disabled:opacity-50"
                        disabled={loading}
                        {...register('qaBranch')}
                      />
                    )}
                  </FormField>
                  <FormField id="connect-uat-branch" label="UAT branch (optional)">
                    {branches.length > 0 ? (
                      <Controller
                        name="uatBranch"
                        control={control}
                        render={({ field }) => (
                          <DropdownSearch
                            id="connect-uat-branch"
                            options={[{ name: '' }, ...branches]}
                            value={
                              field.value
                                ? (branches.find((b) => b.name === field.value) ?? {
                                    name: field.value,
                                  })
                                : { name: '' }
                            }
                            onChange={(b) => field.onChange(b?.name ?? '')}
                            getOptionValue={(b) => b.name}
                            getOptionLabel={(b) => b.name || '— None —'}
                            placeholder="Optional"
                            disabled={loading}
                            className="mb-1"
                          />
                        )}
                      />
                    ) : (
                      <input
                        id="connect-uat-branch"
                        type="text"
                        placeholder="Optional"
                        className="input-mdl disabled:opacity-50"
                        disabled={loading}
                        {...register('uatBranch')}
                      />
                    )}
                  </FormField>
                </div>
              )}
              <div className="flex justify-end gap-3 mt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={loading}
                  className="btn-secondary-mdl"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !selectedRepo || !canSubmitBranch}
                  className="btn-primary-mdl flex items-center"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Connecting…
                    </>
                  ) : (
                    'Add repository'
                  )}
                </button>
              </div>
            </form>
          </>
        ) : null}

        {!canUseDiscovered ? (
          <div className={canUseDiscovered ? 'mt-4 pt-4 border-t border-dark-border' : ''}>
            {!canUseDiscovered && !loadingDiscovered && (
              <p className="text-sm text-slate-400 mb-3">Add a repository by URL below.</p>
            )}
            <form onSubmit={handleAddByUrl} noValidate>
              <FormField
                id="project-repo-input"
                label="Repository URL or owner/repo"
                className="mb-3"
              >
                <input
                  type="text"
                  id="project-repo-input"
                  placeholder="owner/repo or https://github.com/owner/repo"
                  className="input-mdl disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading}
                  {...register('repository', { required: true })}
                />
              </FormField>
              {repoIdentifier && (
                <div className="mb-3 space-y-3">
                  <FormField
                    id="connect-url-deployment-branches"
                    label="Deployment branches (required)"
                  >
                    <p className="text-xs text-slate-400 mb-2">
                      First selected branch is primary for DORA labels and tech stack scans.
                    </p>
                    {loadingBranches ? (
                      <div className="text-sm text-slate-400 py-1">Loading branches…</div>
                    ) : branches.length > 0 ? (
                      <MultiSelectDropdownSearch
                        id="connect-url-deployment-branches"
                        options={branches}
                        selectedIds={deploymentBranches}
                        onChange={setDeploymentBranches}
                        getOptionValue={(b) => b.name}
                        getOptionLabel={(b) => b.name}
                        getOptionSublabel={(b) => (b.isDefault ? '(GitHub default)' : '')}
                        placeholder="Search branches to add…"
                        disabled={loading}
                        comboboxAriaLabel="Deployment branches"
                      />
                    ) : (
                      <input
                        id="connect-url-deployment-branches"
                        type="text"
                        value={customDeploymentBranchesText}
                        onChange={(e) => setCustomDeploymentBranchesText(e.target.value)}
                        placeholder={defaultBranch || 'e.g. main, release'}
                        className="input-mdl disabled:opacity-50"
                        disabled={loading}
                      />
                    )}
                  </FormField>
                  <FormField id="connect-url-qa-branch" label="QA branch (optional)">
                    {branches.length > 0 ? (
                      <Controller
                        name="qaBranch"
                        control={control}
                        render={({ field }) => (
                          <DropdownSearch
                            id="connect-url-qa-branch"
                            options={[{ name: '' }, ...branches]}
                            value={
                              field.value
                                ? (branches.find((b) => b.name === field.value) ?? {
                                    name: field.value,
                                  })
                                : { name: '' }
                            }
                            onChange={(b) => field.onChange(b?.name ?? '')}
                            getOptionValue={(b) => b.name}
                            getOptionLabel={(b) => b.name || '— None —'}
                            placeholder="Optional"
                            disabled={loading}
                            className="mb-1"
                          />
                        )}
                      />
                    ) : (
                      <input
                        id="connect-url-qa-branch"
                        type="text"
                        placeholder="Optional"
                        className="input-mdl disabled:opacity-50"
                        disabled={loading}
                        {...register('qaBranch')}
                      />
                    )}
                  </FormField>
                  <FormField id="connect-url-uat-branch" label="UAT branch (optional)">
                    {branches.length > 0 ? (
                      <Controller
                        name="uatBranch"
                        control={control}
                        render={({ field }) => (
                          <DropdownSearch
                            id="connect-url-uat-branch"
                            options={[{ name: '' }, ...branches]}
                            value={
                              field.value
                                ? (branches.find((b) => b.name === field.value) ?? {
                                    name: field.value,
                                  })
                                : { name: '' }
                            }
                            onChange={(b) => field.onChange(b?.name ?? '')}
                            getOptionValue={(b) => b.name}
                            getOptionLabel={(b) => b.name || '— None —'}
                            placeholder="Optional"
                            disabled={loading}
                            className="mb-1"
                          />
                        )}
                      />
                    ) : (
                      <input
                        id="connect-url-uat-branch"
                        type="text"
                        placeholder="Optional"
                        className="input-mdl disabled:opacity-50"
                        disabled={loading}
                        {...register('uatBranch')}
                      />
                    )}
                  </FormField>
                </div>
              )}
              <div className="flex justify-end gap-3">
                <button
                  type="submit"
                  disabled={loading || !repository.trim() || !canSubmitBranch}
                  className="btn-primary-mdl flex items-center"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Connecting…
                    </>
                  ) : (
                    'Add by URL'
                  )}
                </button>
              </div>
            </form>
          </div>
        ) : null}
      </>
    </Modal>
  );
};

export default ConnectRepositoryToProject;
